import {
  AdminHeader,
  AdminSpinner,
  Box,
  Disclaimer,
  Div,
  H3,
  H5,
  ProgressMeter,
  RestaurantReferral,
  lightTheme,
  restaurantReferralStatusColor,
  RestaurantReferralStatusPill,
} from '@vgrubs/components';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useParams } from 'react-router-dom';
import { ReferralSideCard } from './components/ReferralSideCard';
import { ReferralTimeline } from './components/ReferralTimeline';
import { useReferral } from './hooks/useReferral';

/**
 * One referral: both restaurants with their owners and bonuses, how far the
 * new one is with its orders, and the dates it went through. Read only - the
 * payout is the daily job's, never a button here.
 */
export const ReferralDetails = () => {
  const { id } = useParams() as { id: string };
  const { referral, isLoading, notFound } = useReferral(id);

  return (
    <>
      <AdminHeader title="Referral Details" />
      {isLoading && <AdminSpinner />}

      {notFound && (
        <NoDataComponent
          title="Referral not found"
          description="It does not exist, or it is not part of the program."
        />
      )}

      {referral && (
        <Div $flex $column $gap="24px" $gapSM="8px" $m="32px 0" $mSM="20px 0">
          <OrdersProgress referral={referral} />

          <Div
            $grid
            $gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            $gridTemplateColumnsSM="minmax(0, 1fr)"
            $gap="24px"
            $gapSM="8px"
          >
            <ReferralSideCard
              title="Referred by"
              side={referral.referrer}
              status={referral.status}
            />
            <ReferralSideCard
              title="New restaurant"
              side={referral.referred}
              status={referral.status}
            />
          </Div>

          <Box>
            <H5 $mb="16">Timeline</H5>
            <ReferralTimeline referral={referral} />
          </Box>
        </Div>
      )}
    </>
  );
};

/**
 * The one number the program turns on. Every brand's completed orders count,
 * manual ones do not, and only those placed between going live and the
 * deadline - which is what the small print says, since support gets asked.
 */
const OrdersProgress = ({ referral }: { referral: RestaurantReferral }) => {
  const { status, ordersCount, ordersRequired } = referral;
  const counting = status !== 'signedUp';

  return (
    <Box $flex $column $gap="12px">
      <Div $flex $between $alignCenter $gap="16px">
        <Div $flex $column $gap="2px">
          <Disclaimer $m="0">Qualifying orders</Disclaimer>
          <H3 $m="0">
            {counting
              ? `${Math.min(ordersCount, ordersRequired)} of ${ordersRequired}`
              : `0 of ${ordersRequired}`}
          </H3>
        </Div>
        <RestaurantReferralStatusPill status={status} />
      </Div>
      <ProgressMeter
        value={ordersRequired ? ordersCount / ordersRequired : 0}
        color={restaurantReferralStatusColor(status)}
        label={`${ordersCount} of ${ordersRequired} orders`}
      />
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        {counting
          ? 'Completed orders of every brand at the new restaurant, placed between going live and the deadline. Manual orders do not count.'
          : 'Orders start counting once the new restaurant is verified.'}
      </Disclaimer>
    </Box>
  );
};
